import { Card, CardContent } from "@/components/ui/card";
import { forwardRef, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { TaskMarkdown } from "@/components/TaskMarkdown";
import { useUserContext } from "@/contexts/user";
import { cn } from "@/lib/utils";
import { Folder, Loader2 } from "lucide-react";
import { Dialog } from "@/components/Dialog";
import { config } from "@/config";
import { LinkButton } from "@/components/LinkButton";
import { AudioButton } from "@/components/AudioButton";

type PracticeType = "mental" | "physical" | "emotional" | null;
type CompletedType = {
    id?: number;
    emotional: boolean;
    mental: boolean;
    physical: boolean;
    user_id: string;
};

const practices: Record<PracticeType, { displayName: string, buttonClassName: string, checkboxClassName: string, titleColorClass: string }> = {
    "mental": { displayName: "Mentalt", buttonClassName: "bg-step-3 hover:bg-step-3/90", checkboxClassName: "data-[state=checked]:bg-[#3e6d59]", titleColorClass: "text-step-3" },
    "emotional": { displayName: "Emotionellt", buttonClassName: "bg-step-2 hover:bg-step-2/90", checkboxClassName: "", titleColorClass: "text-step-2" },
    "physical": { displayName: "Fysiskt", buttonClassName: "bg-step-1 hover:bg-step-1/90", checkboxClassName: "data-[state=checked]:bg-step-1", titleColorClass: "text-step-1" },
};

const audioButtons: { title: string, url: string }[] = [
    { title: "Rygg", url: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/public/daretolive.se/BOLLAR_RYGG.mp3" },
    { title: "Höft", url: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/public/daretolive.se/BOLLAR_SATE.mp3" },
    { title: "Ben", url: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/public/daretolive.se/BOLLAR_BENEN.mp3" },
    { title: "Vader", url: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/public/daretolive.se/BOLLAR_VADER.mp3" },
    { title: "Alla i ett flöde", url: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/public/daretolive.se/BOLLAR_SAMMANSLAGET.mp3" },
];

const PracticeForBalanceSection = forwardRef<HTMLElement>(function DailyDareSection(_, ref) {
    const [selectedType, setSelectedType] = useState<PracticeType>(null);
    const [text, setText] = useState<Record<PracticeType, string>>(null);
    const [completed, setCompletedState] = useState<CompletedType>(null);
    const { user } = useUserContext();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const { data: textData, error: testError } = await supabase
                .from("practice_for_balance")
                .select("mental, emotional, physical")
                .limit(1);

            if (testError) {
                console.error("practice_for_balance", testError);
                return;
            }
            setText(textData[0]);

            const { data: completedData, error: completedError } = await supabase
                .from("todays_completed_practice_for_balance")
                .select("id,emotional,mental,physical,user_id")
                .eq("user_id", user.id)
                .limit(1);
            if (completedError) {
                console.error("completed_practice_for_balance", completedError);
                return;
            }

            setCompletedState(completedData?.[0] ?? {
                user_id: user.id,
                emotional: false,
                mental: false,
                physical: false,
            });

            setLoading(false);
        })();
    }, [user.id]);

    const onButtonClicked = (type: PracticeType) => {
        setSelectedType(selectedType !== type ? type : null);
    };

    const updateCompletedState = async (newCompletedState: typeof completed) => {
        const { data, error } = await supabase.from("completed_practice_for_balance").upsert(newCompletedState).select("id");
        if (error) {
            console.error("upsert completed_practice_for_balance", error);
            return;
        }

        if (data) setCompletedState(prev => ({ ...prev, "id": data[0].id }));

    };

    const onCheckedChange = (type: PracticeType, state: boolean) => {
        const newCompletedState = { ...completed, [type]: state };
        setCompletedState(newCompletedState);
        updateCompletedState(newCompletedState);
    };

    return (
        <section className="py-16 bg-muted/30" ref={ref}>
            <div className="container mx-auto px-6">
                <div className="max-w-2xl mx-auto">
                    <Card className="bg-gradient-card border-primary/20 shadow-lg">
                        <CardContent className="p-8 text-center">
                            <div className="flex flex-col items-center justify-center mb-4">
                                {/* <Scale className="w-8 h-8 text-primary mr-3" /> */}
                                <h2 className="text-2xl font-bold text-foreground">Dagliga övningar för balans</h2>
                                <img src={"/goodbalanced_a.png"} className="w-48 h-32 object-cover object-bottom" />
                            </div>
                            <div className="flex flex-wrap justify-center items-center gap-2 mb-6">
                                {loading
                                    ? <>{"Laddar..."}</>
                                    : Object.entries(practices).map(([type, data]: [keyof typeof practices, (typeof practices)[keyof typeof practices]]) => {
                                        return (
                                            <div key={type} className="flex gap-2 items-center">
                                                <Button
                                                    className={cn("font-bold", data.buttonClassName)}
                                                    onClick={() => onButtonClicked(type)}>
                                                    {data.displayName}
                                                </Button>
                                                {/* <Checkbox
                                                    className={data.checkboxClassName}
                                                    checked={completed?.[type] ?? false}
                                                    onCheckedChange={(state) => onCheckedChange(type, state === "indeterminate" ? false : state)}
                                                /> */}
                                            </div>
                                        );
                                    })}
                            </div>
                            <div className="flex flex-col gap-4 items-center w-fit mx-auto">
                                <Dialog
                                    title="Boll practisar"
                                    trigger={
                                        <Button
                                            className="w-full"
                                            variant={"default"}
                                        >
                                            <Folder />
                                            Boll practisar
                                        </Button>
                                    }
                                >
                                    <div className="mt-4 flex flex-col gap-2 items-start">
                                        {audioButtons.map(entry => (
                                            <AudioButton
                                                key={entry.url}
                                                audioTitle={entry.title}
                                                audioUrl={entry.url}
                                                buttonText={entry.title}
                                            />
                                        ))}
                                    </div>
                                </Dialog>

                                <AudioButton
                                    className="w-full"
                                    audioUrl={"https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/sign/audio%20files/KONSTEN_GoodMorning_MEDITATION.wav?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81YmE4YmZlYy0wOTI0LTQ0YjctYmUzZi1kOTc0ZWYwYzk3NDciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyBmaWxlcy9LT05TVEVOX0dvb2RNb3JuaW5nX01FRElUQVRJT04ud2F2IiwiaWF0IjoxNzY0MTUwOTk2LCJleHAiOjMzMjY4NjE0OTk2fQ.iJMbchbiluPB8pB4F17jV7g7FRyxzccSkUvekeF1JO8"}
                                    buttonText={"Meditation - Morgon"}
                                    audioTitle={"Meditation - Morgon"}
                                />

                                <AudioButton
                                    className="w-full"
                                    audioUrl={"https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/sign/audio%20files/KONSTEN_GoodNight_MEDITATION.wav?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81YmE4YmZlYy0wOTI0LTQ0YjctYmUzZi1kOTc0ZWYwYzk3NDciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyBmaWxlcy9LT05TVEVOX0dvb2ROaWdodF9NRURJVEFUSU9OLndhdiIsImlhdCI6MTc2NDE1MTAxNCwiZXhwIjozMzI2ODYxNTAxNH0.wXGkr2NGwid1HuZQ_q08hqiCaDzP2al4yhGJl2YAXnc"}
                                    buttonText={"Meditation - Kväll"}
                                    audioTitle={"Meditation - Kväll"}
                                />

                                <LinkButton
                                    href="https://daretolive.se/practisar"
                                    trigger={
                                        <Button
                                            className="w-full"
                                            variant={"default"}
                                        >
                                            <Folder />
                                            Fler practisar
                                        </Button>
                                    }
                                />
                            </div>
                            <div className="mb-6 text-left">
                                {selectedType && (
                                    <>
                                        <TaskMarkdown
                                            textColor={practices[selectedType].titleColorClass}
                                            suppressProviderWarnings={true}
                                            paragraphSpacing="1em"
                                        >
                                            {`## **Tre enkla...**\n${text?.[selectedType]}`}
                                        </TaskMarkdown>
                                    </>
                                )}
                            </div>
                            {/* <div className="text-sm text-muted-foreground/70">
                                💭 Practisar för {new Date().toLocaleDateString('sv-SE', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </div> */}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </section>
    );
});
export default PracticeForBalanceSection;